import { getChatSession, getChatSubmission, sendChatMessage } from '../../api/chatApi';
import { ChatConnectionError, isChatConnectionError, isNetworkFailureResponse, sendTurnLanded } from './chatConnectionError';
import { ensureOkResponse } from './chatMessageModel';
import { STALLED_TURN_MS } from './useStalledChatTurns';

function isJoblessProcessing(message) {
  return message?.job_status === 'processing' && !message.job_id;
}

function landedAssistant(session, text) {
  const history = session.messages || [];
  const userIndex = history.map((message) => message.role === 'user'
    && String(message.content || '').trim() === text.trim()).lastIndexOf(true);
  return history.slice(userIndex + 1).find((message) => message.role === 'assistant');
}

/**
 * Post one chat turn. When the browser loses the reply (FE #746), the server may
 * still have accepted the turn, so re-read the session before calling it failed:
 * resolves `{ landedSession }` when the turn is there, `{ data }` on a normal reply,
 * and throws a ChatConnectionError when neither can be confirmed.
 */
export async function submitChatTurn({ sessionId, text, idempotencyKey, priorMessages, isCancelled = () => false }) {
  const response = await sendChatMessage(sessionId, text, idempotencyKey);
  if (isCancelled()) return {};
  if (!isNetworkFailureResponse(response)) {
    return { data: ensureOkResponse(response, 'Unable to submit chat message.') };
  }

  const sessionResponse = await getChatSession(sessionId);
  if (isNetworkFailureResponse(sessionResponse) || !sessionResponse || sessionResponse.status >= 400) {
    throw new ChatConnectionError();
  }
  const sessionData = sessionResponse.data || {};
  if (sessionData.session_id === sessionId && sendTurnLanded(sessionData.messages, priorMessages, text)) {
    const assistant = landedAssistant(sessionData, text);
    return { landedSession: sessionData, pendingMessage: isJoblessProcessing(assistant) ? assistant : null };
  }
  throw new ChatConnectionError();
}

// Both recovery paths read serially. The independent deadline also closes out a
// GET that never settles, and stop() prevents late replies from reaching the turn.
function pollChatTurn({ read, isResolved, startedAt, isCancelled, onResolved, onStalled, shouldRetry = () => true }) {
  let stopped = false;
  let pollTimer;
  let deadlineTimer;
  let finish;
  let fail;
  const done = new Promise((resolve, reject) => { finish = resolve; fail = reject; });
  const stop = (data) => {
    stopped = true;
    clearTimeout(pollTimer);
    clearTimeout(deadlineTimer);
    finish(data);
  };
  const cancelled = () => stopped || isCancelled();
  deadlineTimer = setTimeout(() => {
    if (!cancelled()) onStalled();
    stop();
  }, Math.max(0, startedAt + STALLED_TURN_MS - Date.now()));

  async function readNext() {
    if (cancelled()) { stop(); return; }
    try {
      const data = await read();
      if (cancelled()) { stop(); return; }
      if (isResolved(data)) {
        clearTimeout(deadlineTimer);
        await onResolved?.(data, cancelled);
        stop(data);
        return;
      }
    } catch (error) {
      if (!cancelled() && !shouldRetry(error)) {
        fail(error);
        stop();
        return;
      }
    }
    if (cancelled()) stop();
    else pollTimer = setTimeout(readNext, 1000);
  }
  pollTimer = setTimeout(readNext, 1000);
  return { done, stop };
}

// Lost-POST reconciliation retains its session/history semantics, including
// retrying failed reads without offering another POST.
export function reconcileChatTurn({ sessionId, pendingMessage, ...options }) {
  return pollChatTurn({
    ...options,
    read: async () => ensureOkResponse(await getChatSession(sessionId), 'Unable to load chat session.'),
    isResolved: (data) => {
      const assistant = (data.messages || []).find((message) => message.role === 'assistant'
        && message.submission_id === pendingMessage.submission_id);
      return data.session_id === sessionId && assistant && !isJoblessProcessing(assistant);
    },
  });
}

// Unlike session reads, this endpoint can resume a leased submission. A dropped
// read is retried; an HTTP refusal is surfaced to the same catch as a refused POST.
export function followChatSubmission({ submissionId, ...options }) {
  if (!submissionId) throw new Error('Unable to load chat submission.');
  return pollChatTurn({
    ...options,
    read: async () => ensureOkResponse(await getChatSubmission(submissionId), 'Unable to load chat submission.'),
    isResolved: (data) => data.status !== 'processing' || Boolean(data.job_id),
    shouldRetry: isChatConnectionError,
  });
}
